/**
 * General Design Token Utilities
 *
 * Low-level utilities for generating CSS custom properties and var() functions.
 * This matches the SCSS _utils/_tokens.scss implementation.
 */

import { CssValue, NativeCssValue, TokenCategory, TokenName } from './types';

const NativeCssValues = new Set(['currentColor', 'initial', 'inherit', 'unset'] as NativeCssValue[]);

// Design token prefix used in the SCSS system
const DESIGN_TOKEN_PREFIX = 'p-';

/**
 * Generates a CSS custom property name following the design system naming convention
 * Pattern: --p-{category}-{token-name}
 *
 * Matches SCSS: design-token-variable($category, $token-name)
 *
 * @param category - The token category (e.g., 'color', 'spacing')
 * @param tokenName - The token name (e.g., 'functional-default', 'md')
 * @returns The CSS custom property name
 */
export function designTokenVariable(category: TokenCategory, tokenName: TokenName): `--${string}` {
    return `--${DESIGN_TOKEN_PREFIX}${category}-${tokenName}`;
}

/**
 * Generates a CSS var() function call for a design token
 *
 * Matches SCSS: design-token-variable($category, $token-name) usage with fallback
 *
 * @param category - The token category
 * @param tokenName - The token name
 * @param fallback - Optional fallback value (defaults to 'initial')
 * @returns The CSS var() function call or native CSS value.
 */
export function designTokenValue(category: TokenCategory, tokenName: TokenName, fallback: string = 'initial'): CssValue {
    if (NativeCssValues.has(tokenName as NativeCssValue)) return tokenName as NativeCssValue;

    const variableName = designTokenVariable(category, tokenName);

    return `var(${variableName}, ${fallback})`;
}

/**
 * Generic high-order function that creates type-safe token extractor functions for a specific category
 *
 * This is the foundational function that concept-specific implementations use
 *
 * @param category - The token category
 * @returns A function that extracts variables by name with type safety
 */
export function createTokenExtractor<T extends TokenName = TokenName>(category: TokenCategory): TokenExtractor<T> {
    return function (tokenName: T, fallback?: string): CssValue {
        return designTokenValue(category, tokenName, fallback);
    };
}

/**
 * Maps an object of token names to their corresponding CSS var() functions or native CSS values.
 *
 * Matches SCSS: map-to-design-token-variables($map, $category)
 *
 * @param entries - Object with token names as keys and values
 * @param category - The token category
 * @returns Object with same keys but CSS var() values or native CSS values.
 */
export function mapToDesignTokenVariables<T extends Record<string, any>>(entries: T, category: TokenCategory): Record<keyof T, CssValue> {
    const result: Record<keyof T, CssValue> = {} as Record<keyof T, CssValue>;

    for (const [key, value] of Object.entries(entries)) {
        result[key as keyof T] = designTokenValue(category, value);
    }

    return result;
}

/**
 * Generates a CSS custom property name for pseudo states
 * Pattern: --pseudo-{state}-{property}
 *
 * @param state - The pseudo state (e.g., 'hover', 'focus')
 * @param property - The CSS property name
 * @returns The pseudo-prefixed CSS custom property name
 */
export function pseudoStateVariable(state: string, property: string): `--${string}` {
    return `--pseudo-${state}-${property}`;
}

/**
 * Generic utility type for token extractor functions with type safety
 */
export type TokenExtractor<Name extends TokenName = TokenName> = (tokenName: Name, fallback?: string) => CssValue;
